// buildRecap (0027 P4; 0164 N1, founder ruling R4): turns the run record's
// landed counts + the layers the pass closed into the founder-facing headline,
// plus optional lines under it: the earlier answers the run handed back for a
// covered-by check (R4), the decisions the run withdrew because a new answer
// replaced them (0157-15, founder ruling Q11), the saved choices it could
// not apply (0159 F3) and the layers it settled. This file is the ONE home of
// the recap's lines. Pure + structurally typed so it's trivially testable and
// doesn't couple to the SSE state shape.
// 0051 slice 2: the recap reads the run record through the product's next step (its counts and the
// layers the pass closed), never the tab's memory.

// RecapCounts (0164 N1): what the run's committing tree holds, as the run
// record carries it - run_complete on the live path, GET /runs/{rid} on the
// record path, the server omitting each at 0. questionsAdded / decisionsAdded
// are the CREATE-born questions at needs_user and decisions at proposed that
// landed (never the create stage's stats, which count what CREATE composed);
// answersReturned is how many earlier answers the run handed back for a
// covered-by check (founder ruling R4). Absent reads 0.
export type RecapCounts = {
  questionsAdded?: number;
  decisionsAdded?: number;
  answersReturned?: number;
};

export type Recap = {
  // Possibly empty: a no-op recap with returned answers has no headline, and
  // its returned-answer line leads (0051 slice 2).
  headline: string;
  questionsAdded: number;
  decisionsAdded: number;
  // 0164 R4: how many earlier answers this run handed back for a covered-by check.
  answersReturned: number;
  ringName?: string;
  isNoOp: boolean;
  // 0164, founder ruling R4: the earlier answers the run handed back for a
  // covered-by check, as the founder reads it; absent when none came back.
  returnedAnswerLine?: string;
  // 0159 F3: how many saved choices the run could not apply, as the founder
  // reads it; absent when none were set aside.
  setAsideLine?: string;
  // 0157-15, founder ruling Q11: the decisions the run withdrew because a new
  // answer replaced them, as the founder reads it; absent when none.
  withdrawnLine?: string;
  // 0051 slice 2: every layer the pass closed, as the line that opens what was
  // settled (settledLine), and the same line with no link clause for when there
  // is nothing to open (settledPlain); both absent when the pass closed none.
  settledLine?: string;
  settledPlain?: string;
};

// setAsideLineFor is the recap's line for the saved choices a run's settle set
// aside (0159 F3 (a)); undefined for 0 or no count.
function setAsideLineFor(n: number | undefined): string | undefined {
  if (n === undefined || n <= 0) return undefined;
  if (n === 1) return "1 saved choice wasn't applied. See Changes.";
  return `${n} saved choices weren't applied. See Changes.`;
}

// withdrawnLineFor is the recap's line for the decisions a run withdrew because
// a new answer replaced them (0157-15, founder ruling Q11), on the set-aside
// line's pattern; undefined for 0 or no count.
function withdrawnLineFor(n: number | undefined): string | undefined {
  if (n === undefined || n <= 0) return undefined;
  if (n === 1) return "1 decision was withdrawn. See Changes.";
  return `${n} decisions were withdrawn. See Changes.`;
}

// recapIsNoOp is the recap's no-op rule (0164 N1): nothing landed for the
// founder to answer or confirm. RunProgress's end headline reads the same rule.
export function recapIsNoOp(questionsAdded: number | undefined, decisionsAdded: number | undefined): boolean {
  return (questionsAdded ?? 0) <= 0 && (decisionsAdded ?? 0) <= 0;
}

// returnedAnswerLineFor is the recap's line for the earlier answers a run
// handed back for a covered-by check (0164, founder ruling R4); undefined for
// 0 or no count.
function returnedAnswerLineFor(n: number | undefined): string | undefined {
  if (n === undefined || n <= 0) return undefined;
  if (n === 1) return "1 earlier answer may already be covered by your spec. Check it →";
  return `${n} earlier answers may already be covered by your spec. Check them →`;
}

// settledSubject names every layer in the order given, commas between and
// "and" before the last, with the verb that agrees ("Viability is", "Who & Why
// and How & What are"); undefined for none.
function settledSubject(names: readonly string[]): string | undefined {
  if (names.length === 0) return undefined;
  if (names.length === 1) return `${names[0]} is`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} are`;
}

// settledLineFor is the recap's line for the layers a pass closed, with the
// link clause that opens what was settled (0051 slice 2, RULED 2026-10-07);
// undefined when the pass closed none.
export function settledLineFor(names: readonly string[]): string | undefined {
  const subject = settledSubject(names);
  return subject === undefined ? undefined : `${subject} settled · read what we settled →`;
}

// settledPlainFor is the same line with no link clause, ending in a full stop,
// for when the page holds no layer the line opens (0051 slices 2-3, RULED 2026-10-07);
// undefined when the pass closed none.
export function settledPlainFor(names: readonly string[]): string | undefined {
  const subject = settledSubject(names);
  return subject === undefined ? undefined : `${subject} settled.`;
}

// closedLayerNames are the layers the pass closed, in layer order (the served
// next step's closed_layer_names); the first anchors the headline.
export function buildRecap(
  counts: RecapCounts,
  closedLayerNames: readonly string[],
  setAsideCount?: number,
  withdrawnCount?: number,
): Recap {
  const questionsAdded = counts.questionsAdded ?? 0;
  const decisionsAdded = counts.decisionsAdded ?? 0;
  const answersReturned = counts.answersReturned ?? 0;

  // The first layer the pass closed gives the recap a "in <layer>" anchor.
  // Falls back to no layer name.
  const ringName: string | undefined = closedLayerNames[0];

  const isNoOp = recapIsNoOp(questionsAdded, decisionsAdded);
  let headline: string;
  if (isNoOp) {
    // FD-1 (c), and only when no earlier answer came back (0051 slice 2):
    // with one, the returned-answer line leads and there is no headline.
    headline = answersReturned === 0 ? "All caught up. Nothing new needs you." : "";
  } else {
    // 0164, founder ruling R4: what waits on the founder, not "Added" (a
    // drafted decision is not in the spec until he confirms it). Each part is
    // omitted at 0 and singular at 1.
    const parts: string[] = [];
    if (questionsAdded > 0) {
      parts.push(`${questionsAdded} new ${questionsAdded === 1 ? "question" : "questions"} to answer`);
    }
    if (decisionsAdded > 0) {
      parts.push(`${decisionsAdded} ${decisionsAdded === 1 ? "decision" : "decisions"} to confirm`);
    }
    headline = `${parts.join(" and ")}${ringName ? ` in "${ringName}"` : ""}.`;
  }

  const recap: Recap = { headline, questionsAdded, decisionsAdded, answersReturned, ringName, isNoOp };
  // On every recap, a no-op one included.
  const returned = returnedAnswerLineFor(answersReturned);
  if (returned !== undefined) recap.returnedAnswerLine = returned;
  const line = setAsideLineFor(setAsideCount);
  if (line !== undefined) recap.setAsideLine = line;
  // The headline is unchanged by it: a withdraw-only run still reads as a
  // no-op, with the line under it.
  const withdrawn = withdrawnLineFor(withdrawnCount);
  if (withdrawn !== undefined) recap.withdrawnLine = withdrawn;
  const settledLine = settledLineFor(closedLayerNames);
  if (settledLine !== undefined) recap.settledLine = settledLine;
  const settledPlain = settledPlainFor(closedLayerNames);
  if (settledPlain !== undefined) recap.settledPlain = settledPlain;
  return recap;
}
